import { ToastProvider, TooltipProvider } from '@provenance/design';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Register } from './routes/Register';
import { Certificate } from './routes/Certificate';
import { Respond } from './routes/Respond';
import { Submit } from './routes/Submit';
import { Upload } from './routes/Upload';
import { Verify } from './routes/Verify';
import { RELEASE_LABEL } from './release';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

/**
 * The public app: two pages, no session, no navigation chrome.
 *
 * Both are single-purpose landing pages reached from outside — a link in an email, a QR
 * code on a certificate, a chamber's website. Wrapping them in an application shell with
 * a nav rail would suggest there is somewhere else to go, and there is not.
 */
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <ToastProvider>
          {/* A skip link and a <main> landmark, even though these pages have no nav rail
              to skip past. A screen reader user navigating by landmark finds nothing to
              jump to without them, and lands in the page header every time — on the two
              surfaces most likely to be reached by someone who did not choose to use this
              product, and who cannot ask an administrator for help (T-082). */}
          <a href="#main" className="pv-skip-link">
            Skip to content
          </a>
          <main id="main">
          <Routes>
            <Route path="/" element={<Navigate to="/verify" replace />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/register" element={<Register />} />
            {/* Where the secure link in a "we need more information" email lands. */}
            <Route path="/registration/respond/:token" element={<Respond />} />
            <Route path="/respond/:token" element={<Respond />} />
            {/* Self-service upload (spec v4 §25). The chamber page is a dynamic segment,
                so it is declared after every literal route — React Router ranks static
                segments above dynamic ones, but relying on that silently would make
                adding a literal route later a subtle breakage. */}
            <Route path="/upload/:token" element={<Upload />} />
            {/* Where the download button in an approval email lands. */}
            <Route path="/certificate/:token" element={<Certificate />} />
            <Route path="/:chamberSlug/submit" element={<Submit />} />
            <Route path="*" element={<Navigate to="/verify" replace />} />
          </Routes>
          </main>
          <footer className="pv-num px-4 pb-6 pt-2 text-center [font-size:11px] text-[var(--pv-faint)]">
            Provenir · release {RELEASE_LABEL}
          </footer>
        </ToastProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
